/**
 * Document Context Module
 *
 * Provides document state management across the Evidence Extractor application.
 * Handles document listing, uploading, deleting, and refreshing with integration
 * to either the API client or mock service based on demo mode.
 *
 * Requirements: 2.1, 2.2, 2.4, 7.1, 7.2, 7.3, 7.4, 7.5
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { isDemoMode } from '../config';
import {
  ApiClient,
  createApiClient,
  isApiSuccess,
  getErrorMessage,
  type ApiError,
} from '../services/apiClient';
import { getMockDataService, type MockDataService } from '../services/mockDataService';
import { PollingService, createPollingService } from '../services/pollingService';
import type {
  ProcessedDocument,
  Schema,
  PaginatedResponse,
  ApiProcessingJob,
} from '../types/documents';
import type { ProcessingStatus } from '../types/extraction';
import type { Document, UploadedDocument } from '../types';

// ============================================================================
// Types
// ============================================================================

/**
 * Document context value interface
 * Provides document state and operations to consuming components
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 2.1, 2.2, 2.4
 */
export interface DocumentContextValue {
  // Existing state
  /** List of all processed documents for the current user */
  documents: ProcessedDocument[];
  /** Whether documents are currently being loaded */
  isLoading: boolean;
  /** Error that occurred during the last operation, if any */
  error: Error | null;

  // New state for schema management (Requirements: 2.1, 2.4)
  /** List of available extraction schemas */
  schemas: Schema[];
  /** Currently selected schema ID for document processing */
  selectedSchemaId: string | null;

  // New state for processing job tracking (Requirement: 7.2)
  /** Map of active processing jobs by process ID */
  processingJobs: Map<string, ApiProcessingJob>;

  // Updated methods (Requirements: 7.1, 7.2, 7.3, 7.4)
  /** Upload a new document with schema ID */
  uploadDocument: (file: File, schemaId: string, metadataId?: string) => Promise<void>;
  /** Refresh the documents list from the server with pagination */
  refreshDocuments: (page?: number, pageSize?: number) => Promise<void>;
  /** Delete a document by process ID */
  deleteDocument: (processId: string) => Promise<void>;
  /** Download a document by process ID */
  downloadDocument: (processId: string) => Promise<Blob | null>;

  // New methods (Requirements: 2.1, 2.4)
  /** Load available schemas from the server */
  loadSchemas: () => Promise<void>;
  /** Select a schema for document processing */
  selectSchema: (schemaId: string) => void;
  /** Get processing progress for a specific process ID */
  getProcessingProgress: (processId: string) => ApiProcessingJob | undefined;

  // Legacy methods (kept for backward compatibility)
  /** Upload a new version of an existing document */
  uploadNewVersion: (documentId: string, file: File) => Promise<void>;
  /** Get a specific document by ID */
  getDocumentById: (id: string) => ProcessedDocument | undefined;
  /** Clear any existing error */
  clearError: () => void;

  // Pagination state
  /** Current page number */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total count of documents */
  totalCount: number;
  /** Page size */
  pageSize: number;
}

/**
 * Props for the DocumentProvider component
 */
interface DocumentProviderProps {
  /** Child components to wrap with the context */
  children: ReactNode;
}

// ============================================================================
// Context Creation
// ============================================================================

/**
 * Document context with undefined default value
 * Must be used within a DocumentProvider
 */
const DocumentContext = createContext<DocumentContextValue | undefined>(undefined);

// ============================================================================
// Provider Component
// ============================================================================

/**
 * DocumentProvider component
 *
 * Provides document state management to child components.
 * Automatically integrates with API client or mock service based on demo mode.
 * Includes schema management and processing job tracking with polling.
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 2.1, 2.2, 2.4
 *
 * @param props - Provider props containing children
 * @returns Provider component wrapping children
 */
export function DocumentProvider({ children }: DocumentProviderProps): React.ReactElement {
  // ============================================
  // State
  // ============================================

  // Existing state
  const [documents, setDocuments] = useState<ProcessedDocument[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // New state for schema management (Requirements: 2.1, 2.4)
  const [schemas, setSchemas] = useState<Schema[]>([]);
  const [selectedSchemaId, setSelectedSchemaId] = useState<string | null>(null);

  // New state for processing job tracking (Requirement: 7.2)
  const [processingJobs, setProcessingJobs] = useState<Map<string, ApiProcessingJob>>(
    new Map()
  );

  // Pagination state (Requirement: 7.1)
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  // Auth0 hook for API authentication
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // ============================================
  // Memoized Services
  // ============================================

  const apiClient = useMemo<ApiClient | null>(() => {
    if (isDemoMode()) {
      return null;
    }
    return createApiClient(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  const mockService = useMemo<MockDataService | null>(() => {
    if (isDemoMode()) {
      return getMockDataService();
    }
    return null;
  }, []);

  // Polling service ref (created once when apiClient is available)
  const pollingServiceRef = useRef<PollingService | null>(null);

  // Initialize polling service when apiClient is available
  useEffect(() => {
    if (apiClient && !pollingServiceRef.current) {
      pollingServiceRef.current = createPollingService(apiClient);
    }

    // Cleanup on unmount
    return () => {
      if (pollingServiceRef.current) {
        pollingServiceRef.current.stopAllPolling();
      }
    };
  }, [apiClient]);

  // ============================================
  // Schema Management Methods (Requirements: 2.1, 2.4)
  // ============================================

  /**
   * Load available schemas from the server
   * Requirement 2.1: Fetch available schemas on load
   */
  const loadSchemas = useCallback(async (): Promise<void> => {
    setError(null);

    try {
      if (isDemoMode() && mockService) {
        // Demo mode: use mock schemas
        const mockSchemas: Schema[] = [
          {
            id: 'schema-001',
            className: 'SORA_Evidence',
            description: 'Extract SORA evidence from operational documents',
            fileName: 'sora_evidence_schema.json',
            contentType: 'application/json',
            createdOn: '2024-01-01T00:00:00Z',
            updatedOn: '2024-01-15T00:00:00Z',
          },
          {
            id: 'schema-002',
            className: 'Flight_Plan',
            description: 'Extract flight plan details from documents',
            fileName: 'flight_plan_schema.json',
            contentType: 'application/json',
            createdOn: '2024-01-01T00:00:00Z',
            updatedOn: null,
          },
        ];
        setSchemas(mockSchemas);

        // Auto-select first schema if none selected
        if (!selectedSchemaId && mockSchemas.length > 0) {
          setSelectedSchemaId(mockSchemas[0].id);
        }
      } else if (apiClient) {
        // Production mode: call API
        const response = await apiClient.getSchemas();

        if (isApiSuccess(response)) {
          setSchemas(response.data);

          // Auto-select first schema if none selected
          if (!selectedSchemaId && response.data.length > 0) {
            setSelectedSchemaId(response.data[0].id);
          }
        } else {
          throw new Error(getErrorMessage(response.error));
        }
      } else {
        throw new Error('No document service available. Please check your configuration.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load schemas';
      setError(new Error(errorMessage));
      console.error('Error loading schemas:', err);
    }
  }, [apiClient, mockService, selectedSchemaId]);

  /**
   * Select a schema for document processing
   * Requirement 2.4: Allow schema selection
   */
  const selectSchema = useCallback((schemaId: string): void => {
    setSelectedSchemaId(schemaId);
  }, []);

  // ============================================
  // Processing Job Tracking Methods (Requirement: 7.2)
  // ============================================

  /**
   * Update a processing job in the state
   */
  const updateProcessingJob = useCallback((job: ApiProcessingJob): void => {
    setProcessingJobs((prev) => {
      const newMap = new Map(prev);
      newMap.set(job.processId, job);
      return newMap;
    });
  }, []);

  /**
   * Remove a processing job from the state
   */
  const removeProcessingJob = useCallback((processId: string): void => {
    setProcessingJobs((prev) => {
      const newMap = new Map(prev);
      newMap.delete(processId);
      return newMap;
    });
  }, []);

  /**
   * Get processing progress for a specific process ID
   */
  const getProcessingProgress = useCallback(
    (processId: string): ApiProcessingJob | undefined => {
      return processingJobs.get(processId);
    },
    [processingJobs]
  );

  // ============================================
  // Document Operations (Requirements: 7.1, 7.2, 7.3, 7.4, 7.5)
  // ============================================

  /**
   * Refresh documents from the server using paginated endpoint
   * Requirement 7.1: Call paginated processed endpoint and update local state
   */
  const refreshDocuments = useCallback(
    async (page: number = 1, size: number = 20): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        if (isDemoMode() && mockService) {
          // Demo mode: use mock data (convert to ProcessedDocument format)
          const mockDocs = mockService.getDocuments();
          // Transform mock Document[] to ProcessedDocument[]
          const processedDocs: ProcessedDocument[] = mockDocs.map((doc: Document) => ({
            processId: doc.id,
            processedFileName: doc.name,
            processedFileMimeType: doc.mimeType,
            processedTime: doc.updatedAt,
            importedTime: doc.createdAt,
            lastModifiedTime: doc.updatedAt,
            lastModifiedBy: doc.createdBy,
            status: 'completed' as ProcessingStatus,
            entityScore: 0.85,
            minExtractedEntityScore: 0.6,
            schemaScore: 0.9,
            result: null,
            confidence: null,
            targetSchema: null,
            promptTokens: 0,
            completionTokens: 0,
            processOutput: [],
            extractedComparisonData: null,
            comment: null,
          }));
          setDocuments(processedDocs);
          setCurrentPage(1);
          setTotalPages(1);
          setTotalCount(processedDocs.length);
          setPageSize(size);
        } else if (apiClient) {
          // Production mode: call paginated API
          const response = await apiClient.getProcessedDocuments(page, size);

          if (isApiSuccess(response)) {
            const paginatedData: PaginatedResponse<ProcessedDocument> = response.data;
            setDocuments(paginatedData.items);
            setCurrentPage(paginatedData.currentPage);
            setTotalPages(paginatedData.totalPages);
            setTotalCount(paginatedData.totalCount);
            setPageSize(paginatedData.pageSize);
          } else {
            throw new Error(getErrorMessage(response.error));
          }
        } else {
          throw new Error('No document service available. Please check your configuration.');
        }
      } catch (err) {
        // Requirement 7.5: Handle API errors and expose through error state
        const errorMessage = err instanceof Error ? err.message : 'Failed to load documents';
        setError(new Error(errorMessage));
        console.error('Error refreshing documents:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [apiClient, mockService]
  );

  /**
   * Upload a new document with schema ID
   * Requirement 7.2: Use submit endpoint and begin status polling
   * Requirement 2.2: Include Schema_Id in submission
   *
   * @param file - The file to upload
   * @param schemaId - The schema ID to use for extraction
   * @param metadataId - Optional metadata ID for the document
   */
  const uploadDocument = useCallback(
    async (file: File, schemaId: string, metadataId?: string): Promise<void> => {
      setError(null);

      try {
        if (isDemoMode() && mockService) {
          // Demo mode: simulate upload with delay
          const mockServiceImpl = mockService as MockDataService & {
            simulateUpload: (
              file: File,
              name: string,
              classification: Document['classification']
            ) => Promise<UploadedDocument>;
          };

          // Create a mock processing job
          const mockProcessId = `proc-${Date.now()}`;
          const mockJob: ApiProcessingJob = {
            processId: mockProcessId,
            fileName: file.name,
            status: 'processing',
            startedAt: new Date().toISOString(),
          };
          updateProcessingJob(mockJob);

          // Simulate processing delay
          await new Promise((resolve) => setTimeout(resolve, 2000));

          // Simulate upload
          await mockServiceImpl.simulateUpload(file, file.name, 'other');

          // Update job to completed
          updateProcessingJob({
            ...mockJob,
            status: 'completed',
            completedAt: new Date().toISOString(),
          });

          // Refresh to get updated list
          await refreshDocuments();

          // Remove job after a delay
          setTimeout(() => removeProcessingJob(mockProcessId), 5000);
        } else if (apiClient) {
          // Production mode: call submit API
          const response = await apiClient.submitDocument(
            file,
            schemaId,
            metadataId || `metadata-${Date.now()}`
          );

          if (isApiSuccess(response)) {
            // Extract process ID from status_url
            const statusUrl = response.data.status_url;
            const processId = statusUrl.split('/').pop() || `proc-${Date.now()}`;

            // Create processing job
            const job: ApiProcessingJob = {
              processId,
              fileName: file.name,
              status: 'processing',
              startedAt: new Date().toISOString(),
            };
            updateProcessingJob(job);

            // Add a placeholder document to the list so it appears immediately
            const placeholderDoc: ProcessedDocument = {
              processId,
              processedFileName: file.name,
              processedFileMimeType: file.type || 'application/pdf',
              processedTime: null,
              importedTime: new Date().toISOString(),
              lastModifiedTime: new Date().toISOString(),
              lastModifiedBy: null,
              status: 'processing',
              entityScore: 0,
              minExtractedEntityScore: 0,
              schemaScore: 0,
              result: null,
              confidence: null,
              targetSchema: null,
              promptTokens: 0,
              completionTokens: 0,
              processOutput: [],
              extractedComparisonData: null,
              comment: null,
            };
            setDocuments((prev) => [placeholderDoc, ...prev]);
            setTotalCount((prev) => prev + 1);

            // Start polling for status updates
            if (pollingServiceRef.current) {
              pollingServiceRef.current.startPolling(
                processId,
                // onProgress callback - update both the job and the document in the list
                (status: ProcessingStatus) => {
                  updateProcessingJob({
                    ...job,
                    status,
                  });
                  // Update the document's status in the documents array for real-time UI updates
                  setDocuments((prev) =>
                    prev.map((doc) =>
                      doc.processId === processId ? { ...doc, status } : doc
                    )
                  );
                },
                // onComplete callback
                (result: ProcessedDocument) => {
                  updateProcessingJob({
                    ...job,
                    status: 'completed',
                    completedAt: new Date().toISOString(),
                  });
                  // Refresh documents to include the new one
                  refreshDocuments();
                  // Remove job after a delay
                  setTimeout(() => removeProcessingJob(processId), 5000);
                },
                // onError callback
                (apiError: ApiError) => {
                  updateProcessingJob({
                    ...job,
                    status: 'failed',
                    error: apiError.message,
                  });
                  setError(new Error(apiError.message));
                }
              );
            }
          } else {
            throw new Error(getErrorMessage(response.error));
          }
        } else {
          throw new Error('No document service available. Please check your configuration.');
        }
      } catch (err) {
        // Requirement 7.5: Handle API errors and expose through error state
        const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
        setError(new Error(errorMessage));
        console.error('Error uploading document:', err);
        throw err; // Re-throw so callers can handle it
      }
    },
    [apiClient, mockService, refreshDocuments, updateProcessingJob, removeProcessingJob]
  );

  /**
   * Delete a document by process ID
   * Requirement 7.3: Call delete endpoint and remove item from local state
   *
   * @param processId - The process ID of the document to delete
   */
  const deleteDocument = useCallback(
    async (processId: string): Promise<void> => {
      setError(null);

      try {
        if (isDemoMode() && mockService) {
          // Demo mode: simulate delete
          const mockServiceImpl = mockService as MockDataService & {
            deleteDocument: (id: string) => void;
          };

          mockServiceImpl.deleteDocument(processId);

          // Update local state immediately
          setDocuments((prev) => prev.filter((doc) => doc.processId !== processId));
          setTotalCount((prev) => Math.max(0, prev - 1));
        } else if (apiClient) {
          // Production mode: call delete API
          const response = await apiClient.deleteProcessedDocument(processId);

          if (isApiSuccess(response)) {
            // Update local state immediately
            setDocuments((prev) => prev.filter((doc) => doc.processId !== processId));
            setTotalCount((prev) => Math.max(0, prev - 1));
          } else {
            throw new Error(getErrorMessage(response.error));
          }
        } else {
          throw new Error('No document service available. Please check your configuration.');
        }
      } catch (err) {
        // Requirement 7.5: Handle API errors and expose through error state
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete document';
        setError(new Error(errorMessage));
        console.error('Error deleting document:', err);
        throw err; // Re-throw so callers can handle it
      }
    },
    [apiClient, mockService]
  );

  /**
   * Download a document by process ID
   * Requirement 7.4: Stream file from files endpoint
   *
   * @param processId - The process ID of the document to download
   * @returns The document blob or null if not found
   */
  const downloadDocument = useCallback(
    async (processId: string): Promise<Blob | null> => {
      setError(null);

      try {
        if (isDemoMode() && mockService) {
          // Demo mode: simulate download by creating a mock blob
          const doc = documents.find((d) => d.processId === processId);
          if (!doc) {
            throw new Error('Document not found');
          }

          // Create a simulated PDF blob with placeholder content
          const mockContent = `[Demo Mode] This is a simulated download for: ${doc.processedFileName}\n\nIn production, this would be the actual document content from Azure Blob Storage.`;
          const blob = new Blob([mockContent], { type: 'text/plain' });
          return blob;
        } else if (apiClient) {
          // Production mode: call streaming API
          const response = await apiClient.streamOriginalFile(processId);

          if (isApiSuccess(response)) {
            return response.data;
          } else {
            throw new Error(getErrorMessage(response.error));
          }
        } else {
          throw new Error('No document service available. Please check your configuration.');
        }
      } catch (err) {
        // Requirement 7.5: Handle API errors and expose through error state
        const errorMessage = err instanceof Error ? err.message : 'Failed to download document';
        setError(new Error(errorMessage));
        console.error('Error downloading document:', err);
        return null;
      }
    },
    [apiClient, mockService, documents]
  );

  // ============================================
  // Legacy Methods (kept for backward compatibility)
  // ============================================

  /**
   * Upload a new version of an existing document
   * Note: This is a legacy method kept for backward compatibility
   *
   * @param documentId - The ID of the document to update
   * @param file - The new file version
   */
  const uploadNewVersion = useCallback(
    async (documentId: string, file: File): Promise<void> => {
      setError(null);

      try {
        if (isDemoMode() && mockService) {
          // Demo mode: simulate version upload
          const mockServiceImpl = mockService as MockDataService & {
            uploadNewVersion: (documentId: string, file: File) => Promise<Document | undefined>;
          };

          const updatedDoc = await mockServiceImpl.uploadNewVersion(documentId, file);
          if (!updatedDoc) {
            throw new Error('Document not found');
          }

          // Refresh to get updated list
          await refreshDocuments();
        } else if (apiClient) {
          // Production mode: call API
          const response = await apiClient.uploadDocumentVersion(documentId, file);

          if (isApiSuccess(response)) {
            // Refresh to get updated list with full document data
            await refreshDocuments();
          } else {
            throw new Error(getErrorMessage(response.error));
          }
        } else {
          throw new Error('No document service available. Please check your configuration.');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to upload new version';
        setError(new Error(errorMessage));
        console.error('Error uploading new version:', err);
        throw err; // Re-throw so callers can handle it
      }
    },
    [apiClient, mockService, refreshDocuments]
  );

  /**
   * Get a specific document by ID
   *
   * @param id - The document ID to find
   * @returns The document if found, undefined otherwise
   */
  const getDocumentById = useCallback(
    (id: string): ProcessedDocument | undefined => {
      return documents.find((doc) => doc.processId === id);
    },
    [documents]
  );

  /**
   * Clear any existing error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // ============================================
  // Effects
  // ============================================

  // Load documents and schemas on mount and when authentication changes
  useEffect(() => {
    // In demo mode, load immediately
    // In production mode, wait for authentication
    if (isDemoMode() || isAuthenticated) {
      refreshDocuments();
      loadSchemas();
    }
  }, [isAuthenticated, refreshDocuments, loadSchemas]);

  // Resume polling for any documents that are still in-progress after load/refresh
  const pollingResumedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!pollingServiceRef.current || isLoading || documents.length === 0) {
      return;
    }

    const terminalStatuses = new Set(['completed', 'failed']);

    for (const doc of documents) {
      if (terminalStatuses.has(doc.status)) {
        // Clean up tracking if it was previously in-progress
        pollingResumedRef.current.delete(doc.processId);
        continue;
      }

      // Already started polling for this one — skip
      if (pollingResumedRef.current.has(doc.processId)) {
        continue;
      }

      pollingResumedRef.current.add(doc.processId);
      const processId = doc.processId;

      pollingServiceRef.current.startPolling(
        processId,
        // onProgress
        (status: ProcessingStatus) => {
          setDocuments((prev) =>
            prev.map((d) =>
              d.processId === processId ? { ...d, status } : d
            )
          );
        },
        // onComplete
        () => {
          pollingResumedRef.current.delete(processId);
          refreshDocuments();
        },
        // onError
        (apiError: ApiError) => {
          pollingResumedRef.current.delete(processId);
          setDocuments((prev) =>
            prev.map((d) =>
              d.processId === processId ? { ...d, status: 'failed' as ProcessingStatus } : d
            )
          );
          setError(new Error(apiError.message));
        }
      );
    }
    // Only re-run when loading finishes (documents have been refreshed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // ============================================
  // Context Value
  // ============================================

  // Memoized context value
  const contextValue = useMemo<DocumentContextValue>(
    () => ({
      // Existing state
      documents,
      isLoading,
      error,

      // New state for schema management
      schemas,
      selectedSchemaId,

      // New state for processing job tracking
      processingJobs,

      // Updated methods
      uploadDocument,
      refreshDocuments,
      deleteDocument,
      downloadDocument,

      // New methods
      loadSchemas,
      selectSchema,
      getProcessingProgress,

      // Legacy methods
      uploadNewVersion,
      getDocumentById,
      clearError,

      // Pagination state
      currentPage,
      totalPages,
      totalCount,
      pageSize,
    }),
    [
      documents,
      isLoading,
      error,
      schemas,
      selectedSchemaId,
      processingJobs,
      uploadDocument,
      refreshDocuments,
      deleteDocument,
      downloadDocument,
      loadSchemas,
      selectSchema,
      getProcessingProgress,
      uploadNewVersion,
      getDocumentById,
      clearError,
      currentPage,
      totalPages,
      totalCount,
      pageSize,
    ]
  );

  return (
    <DocumentContext.Provider value={contextValue}>
      {children}
    </DocumentContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access the document context
 *
 * @returns DocumentContextValue with document state and operations
 * @throws Error if used outside of DocumentProvider
 *
 * @example
 * ```tsx
 * function DocumentList() {
 *   const {
 *     documents,
 *     isLoading,
 *     error,
 *     refreshDocuments,
 *     schemas,
 *     selectedSchemaId,
 *     selectSchema,
 *     uploadDocument,
 *   } = useDocuments();
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *
 *   return (
 *     <div>
 *       <SchemaSelector
 *         schemas={schemas}
 *         selectedId={selectedSchemaId}
 *         onSelect={selectSchema}
 *       />
 *       <ul>
 *         {documents.map(doc => (
 *           <li key={doc.processId}>{doc.processedFileName}</li>
 *         ))}
 *       </ul>
 *     </div>
 *   );
 * }
 * ```
 */
export function useDocuments(): DocumentContextValue {
  const context = useContext(DocumentContext);

  if (context === undefined) {
    throw new Error('useDocuments must be used within a DocumentProvider');
  }

  return context;
}

// ============================================================================
// Exports
// ============================================================================

export { DocumentContext };
export default DocumentProvider;
